import type { GridApiPremium, GridGroupNode, GridRowId } from "@mui/x-data-grid-premium";
import { GRID_ROOT_GROUP_ID } from "@mui/x-data-grid-premium";
import type { RefObject } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { getAllRowIds, getRowNode } from "@entrilia/shared/utilities/dataGridApiHelper";
import useClientContext from "../../../../../../context/useClientContext";
import type { GridKey, ReportsGridExpandState } from "../../storage/reportsGridExpandStorage";
import { reportsGridExpandStorage } from "../../storage/reportsGridExpandStorage";

const FALLBACK_EXPAND_VALUE = true;

interface Props {
  apiRef: RefObject<GridApiPremium | null>;
  gridKey: GridKey;
}

const useGridExpand = ({ apiRef, gridKey }: Props) => {
  const { clientCode } = useClientContext();
  const [expandState, setExpandState] = useState<ReportsGridExpandState>(() =>
    reportsGridExpandStorage.getReportsGridExpandState()
  );
  const expandStateRef = useRef<ReportsGridExpandState | undefined>(expandState);
  useEffect(() => {
    expandStateRef.current = expandState;
  }, [expandState]);

  const isGroupExpandedByDefault = useCallback(
    (node: GridGroupNode) => {
      const expanded = expandStateRef.current?.[clientCode]?.[gridKey]?.[node.id];
      return expanded !== undefined ? expanded : FALLBACK_EXPAND_VALUE;
    },
    [clientCode, gridKey]
  );

  const toggleSingleGridRow = useCallback(
    (expanded: boolean, rowId: GridRowId) =>
      setExpandState(reportsGridExpandStorage.toggleSingleGridRow(gridKey, clientCode, rowId, expanded)),
    [clientCode, gridKey]
  );

  const toggleAllGridRows = useCallback(
    (expanded: boolean) => {
      const groupRows: GridRowId[] = [];
      getAllRowIds(apiRef).forEach((rowId) => {
        const row = getRowNode(apiRef, rowId);
        if (row?.type === "group" && row?.id !== GRID_ROOT_GROUP_ID) {
          groupRows.push(rowId);
        }
      });
      setExpandState(reportsGridExpandStorage.toggleAllGridRows(gridKey, clientCode, expanded, groupRows));
    },
    // eslint-disable-next-line @eslint-react/exhaustive-deps
    [clientCode, gridKey]
  );

  return {
    isGroupExpandedByDefault,
    toggleSingleGridRow,
    toggleAllGridRows,
  };
};

export default useGridExpand;
